import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import ChallengesForm from "@/UI/forms/ChallengesForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import SidePopup from "@/UI/popups/SidePopup";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

const PAGE_SIZE = 12;

interface Challenge {
    _id: string;
    name: string;
    description: string;
    trigger: "money" | "order" | "product" | "category";
    target: {
        _id: string;
        name: string;
        picture: string;
    } | null;
    count: number;
    days: number | null;
    consecutive: boolean;
    startDate: string;
    endDate: string;
    minPrice: number | null;
    tiers: any[];
    coffeeBeans: number;
    repeatable: boolean;
    type: "system" | "store";
    active: boolean;
}

export default function ChallengesScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // ── Challenges fetch ─────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("challenges", { query: { page: 1, limit: PAGE_SIZE } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: PAGE_SIZE } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: PAGE_SIZE } });
        };
        window.addEventListener("challenges:refresh", handler);
        return () => window.removeEventListener("challenges:refresh", handler);
    }, []);

    const challenges: Challenge[] = data?.data?.challenges ?? [];
    const total: number = data?.data?.total ?? 0;

    // ── Delete fetch ────────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("challenges", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: PAGE_SIZE } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (challenge: Challenge) => {
        const name = "confirm-delete-challenge";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.challenges.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(challenge._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Get-one fetch (duplicate) ────────────────────────────────────────────
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("challenges", { method: "GET" }, false);

    const openDuplicateForm = (challenge: any) => {
        const name = "duplicate-challenge";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.challengeForm.duplicateTitle,
                Content: ChallengesForm,
                contentDetails: {
                    initialData: challenge,
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: PAGE_SIZE } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const challenge = oneData?.data?.challenge;
            if (challenge) openDuplicateForm(challenge);
        }
    }, [fetchingOne]);

    const handleDuplicate = (row: Challenge) => {
        fetchOne({ param: row._id });
    };

    // ── Add Challenge ────────────────────────────────────────────────────────────
    const openAddChallengeForm = () => {
        const name = "add-challenge";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.challengeForm.createTitle,
                Content: ChallengesForm,
                contentDetails: {
                    onSuccess: () => {
                        window.dispatchEvent(new CustomEvent("challenges:refresh"));
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Helper Functions ────────────────────────────────────────────────────────
    const formatDate = (dateString: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    // ── Columns ─────────────────────────────────────────────────────────────────
    const columns: TableColumn<Challenge>[] = [
        {
            key: "name",
            header: t.challenges.col.name,
            width: "180px",
            render: (row) => <TableCell.Text value={row.name || "—"} maxChars={25} />,
        },
        {
            key: "trigger",
            header: t.challenges.col.trigger,
            width: "100px",
            render: (row) => <TableCell.Badge label={t.challenges.triggers[row.trigger]} variant="orange" />,
        },
        {
            key: "target",
            header: t.challenges.col.target,
            width: "140px",
            render: (row) => {
                if (row.trigger === "product" || row.trigger === "category") {
                    return <TableCell.Text value={row.target?.name || "—"} maxChars={20} />;
                }
                return <TableCell.Text value="—" />;
            },
        },
        {
            key: "coffeeBeans",
            header: t.challenges.col.coffeeBeans,
            width: "100px",
            render: (row) => (
                <span className="challenges-points-badge">
                    {row.coffeeBeans.toLocaleString()} {t.challenges.beans}
                </span>
            ),
        },
        {
            key: "dateRange",
            header: t.challenges.col.dateRange,
            width: "180px",
            render: (row) => (
                <div className="challenges-date-range">
                    <span>{formatDate(row.startDate)}</span>
                    <span className="challenges-date-separator">{lang === "ar" ? "←" : "→"}</span>
                    <span>{formatDate(row.endDate)}</span>
                </div>
            ),
        },
        {
            key: "count",
            header: t.challenges.col.count,
            width: "80px",
            render: (row) => <span className="challenges-count-text">{row.count}x</span>,
        },
        {
            key: "active",
            header: t.challenges.col.active,
            width: "100px",
            render: (row) => (
                <span className={`challenges-badge ${row.active ? "challenges-badge--active" : "challenges-badge--inactive"}`}>
                    {row.active ? t.challenges.active : t.challenges.inactive}
                </span>
            ),
        },
        {
            key: "actions",
            header: t.challenges.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    const filterContent = <input className="actions-bar-search-input" placeholder={t.actionsBar.search} />;

    return (
        <div className="challenges-screen">
            <ActionsBar onAdd={openAddChallengeForm} filterContent={filterContent} />
            <div className="challenges-screen-content">
                <Table
                    columns={columns}
                    data={challenges}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
                />
            </div>
        </div>
    );
}
